import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { clockTime } from '../lib/format.js';

/**
 * What actually happened on this thread, read from the append-only events
 * feed the analytics pipelines use. Read-only by construction — an audit trail
 * an agent can edit is not an audit trail.
 */
const LABELS = {
  'conversation.created': 'Conversation started',
  'conversation.status_changed': 'Status changed',
  'message.sent': 'Message sent',
  // What an agent told the visitor, and then unsaid. The trail is the only
  // place the original send and its withdrawal sit next to each other, which
  // is what makes "what did we actually tell them" answerable afterwards.
  'message.edited': 'Message edited',
  'message.deleted': 'Message deleted',
  'message.read': 'Messages read',
  'ticket.created': 'Ticket created',
  'ticket.updated': 'Ticket updated',
  'doc.published': 'Article published',
  'push.sent': 'Push delivered',
  'push.pruned': 'Dead push endpoints pruned',
  'widget.session_started': 'Widget session started',
};

const TONE = {
  'conversation.status_changed': 'accent',
  'message.deleted': 'warn',
  'push.sent': 'ok',
  'push.pruned': 'warn',
  'ticket.created': 'accent',
};

/** The one or two details worth showing inline for each event type. */
function detail(event) {
  const p = event.payload ?? {};

  switch (event.type) {
    case 'conversation.status_changed':
      return `${p.from ?? '?'} → ${p.to ?? '?'}`;
    case 'message.sent':
      return [
        p.isEmergency ? 'EMERGENCY' : null,
        p.hasAttachments ? 'with attachment' : null,
        typeof p.length === 'number' ? `${p.length} chars` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'message.edited':
      return typeof p.length === 'number' ? `now ${p.length} chars` : '';
    case 'message.deleted':
      // What was withdrawn, in the only terms the trail is allowed to keep:
      // the content itself is gone, so its shape is all that is left to say.
      return [
        typeof p.length === 'number' ? `${p.length} chars` : null,
        p.attachments ? `${p.attachments} attachment${p.attachments === 1 ? '' : 's'}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'message.read':
      return p.count ? `${p.count} message${p.count === 1 ? '' : 's'}` : '';
    case 'push.sent':
      return [
        p.count ? `${p.count} device${p.count === 1 ? '' : 's'}` : null,
        p.subscriberType,
        p.isEmergency ? 'EMERGENCY' : null,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'push.pruned':
      return p.count ? `${p.count} endpoint${p.count === 1 ? '' : 's'}` : '';
    case 'widget.session_started':
      return p.origin ?? '';
    case 'ticket.updated':
      return p.deleted
        ? 'deleted'
        : [
            p.statusFrom !== p.statusTo ? `${p.statusFrom} → ${p.statusTo}` : null,
            p.reassigned ? 'reassigned' : null,
          ]
            .filter(Boolean)
            .join(' · ');
    default:
      return '';
  }
}

export function EventTrail({ appId, conversationId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!appId || !conversationId) {
      setEvents([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setExpanded(false);

    api.conversations
      .events(appId, conversationId, { limit: 40 })
      .then(({ data }) => {
        if (!cancelled) setEvents(data);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appId, conversationId]);

  if (loading) return <div className="rail__empty">Loading activity…</div>;
  if (events.length === 0) return <div className="rail__empty">No recorded activity yet.</div>;

  const visible = expanded ? events : events.slice(0, 8);

  return (
    <>
      <div className="trail">
        {visible.map((event) => {
          const info = detail(event);
          return (
            <div className="trail__item" key={event.id}>
              <span className={`trail__pip trail__pip--${TONE[event.type] ?? 'muted'}`} />
              <span className="trail__body">
                <span className="trail__label">
                  {LABELS[event.type] ?? event.type}
                  <span className="trail__actor">{event.actorType}</span>
                </span>
                {info && <span className="trail__detail mono">{info}</span>}
              </span>
              <span className="trail__time mono">{clockTime(event.createdAt)}</span>
            </div>
          );
        })}
      </div>

      {events.length > 8 && (
        <button type="button" className="link-button" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `Show all ${events.length}`}
        </button>
      )}
    </>
  );
}

export default EventTrail;
